import React, { useEffect, useState, useCallback } from 'react';
import { Scales, FloppyDisk, Thermometer, CopySimple, Trash, ChatCircleDots, Plus, Camera, Package, DotsSixVertical } from '@phosphor-icons/react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useAuth } from '../contexts/AuthContext';
import { sortByVCode } from '../utils/sortVolunteers';
import { PhotoCell } from '../components/PhotoCell';
import { PhotoBulkImport } from '../components/PhotoBulkImport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProbesPanel({ studyId, studyTitle, lang }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  const [samples, setSamples] = useState([]);
  const [visits, setVisits] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [weights, setWeights] = useState({});
  const [weightMeta, setWeightMeta] = useState({});
  const [savingW, setSavingW] = useState({});
  const [sondas, setSondas] = useState([]);
  const [extraSondas, setExtraSondas] = useState([]);
  const [sondaMeasurements, setSondaMeasurements] = useState({});
  const [sondaValues, setSondaValues] = useState({});
  const [savingS, setSavingS] = useState({});
  const [loading, setLoading] = useState(true);
  const [openWeight, setOpenWeight] = useState(null);
  const [openSonda, setOpenSonda] = useState(null);
  const [sondaZones, setSondaZones] = useState({});
  const [headerZone, setHeaderZone] = useState('');
  const [savingZone, setSavingZone] = useState(false);
  const [unitConfig, setUnitConfig] = useState({});
  const [confirmDeleteSonda, setConfirmDeleteSonda] = useState(null);
  const [deletingSonda, setDeletingSonda] = useState(false);
  // Sintomas Subjetivos Paciente
  const [openSymptoms, setOpenSymptoms] = useState(false);
  const [symptoms, setSymptoms] = useState([]);
  const [newSymptom, setNewSymptom] = useState({ volunteer_code: '', visit_code: '', observation: '' });
  const [savingSymptom, setSavingSymptom] = useState(false);
  const [editingSymptomId, setEditingSymptomId] = useState(null);
  const [editingSymptomDraft, setEditingSymptomDraft] = useState({});
  // Per-volunteer observations on probes (per sonda_name + volunteer_code)
  const [sondaObservations, setSondaObservations] = useState({});
  const [savingObs, setSavingObs] = useState({});
  // Persisted ordering of sondas (drag & drop)
  const [sondaOrder, setSondaOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // Auto "Fotografias - {device}" probes per imaging device selected in protocol
  const [imagingDevices, setImagingDevices] = useState([]);  // e.g. ['Camara HD', 'Movil', 'AEVA']
  // URS-F217: photos by sonda name (Fotografias - Camara HD), indexed by sondaName -> array of photo docs
  const [volunteerPhotos, setVolunteerPhotos] = useState({});

  const loadPhotos = useCallback(async (sondaName) => {
    if (!sondaName || !sondaName.startsWith('Fotografias')) return;
    const device = sondaName.replace('Fotografias - ', '');
    try {
      const { data } = await axios.get(`${API_URL}/api/studies/${studyId}/volunteer-photos`, {
        withCredentials: true,
        params: { device },
      });
      setVolunteerPhotos(p => ({ ...p, [sondaName]: data || [] }));
    } catch (e) {
      // silent — table will show empty cells
    }
  }, [studyId]);

  // Trigger load when opening a Fotografias dialog
  useEffect(() => {
    if (openSonda && openSonda.startsWith('Fotografias')) {
      loadPhotos(openSonda);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSonda]);
  // Inline dispense dialog (replicates Muestras flow)
  const [openDispense, setOpenDispense] = useState(null); // sample object
  const [dispForm, setDispForm] = useState({ volunteer_id: '', visit_code: '', quantity: 1, date: '', notes: '' });
  const [savingDispense, setSavingDispense] = useState(false);

  const lb = lang === 'es'
    ? { weights: 'Pesos', initial_weight: 'Peso inicial', initial_qty: 'Cantidad inicial', no_samples: 'No hay muestras asociadas a este estudio', no_volunteers: 'No hay voluntarios incluidos', error_exceed_t0: 'El valor no puede superar el valor en T0', saving_ok: 'Valor guardado', sonda_saved: 'Medicion guardada', no_sondas: 'No hay sondas seleccionadas en el protocolo', no_entries: 'No hay sondas ni muestras configuradas', duplicate: 'Duplicar Sonda', duplicated: 'Sonda duplicada', delete_confirm: 'Eliminar esta sonda duplicada y sus mediciones?', deleted: 'Sonda eliminada', zone: 'Zona', zone_placeholder: 'Ej: Antebrazo derecho', zone_save: 'Aplicar zona', zone_saved: 'Zona aplicada a todos los voluntarios', add_unit2: 'Añadir Unidad 2', remove_unit2: 'Quitar Unidad 2', unit2_from: 'Desde visita' }
    : { weights: 'Weights', initial_weight: 'Initial weight', initial_qty: 'Initial qty', no_samples: 'No samples associated with this study', no_volunteers: 'No included volunteers', error_exceed_t0: 'Value cannot exceed T0 value', saving_ok: 'Value saved', sonda_saved: 'Measurement saved', no_sondas: 'No probes selected in protocol', no_entries: 'No probes or samples configured', duplicate: 'Duplicate Probe', duplicated: 'Probe duplicated', delete_confirm: 'Delete this duplicated probe and its measurements?', deleted: 'Probe deleted', zone: 'Zone', zone_placeholder: 'E.g.: Right forearm', zone_save: 'Apply zone', zone_saved: 'Zone applied to all volunteers', add_unit2: 'Add Unit 2', remove_unit2: 'Remove Unit 2', unit2_from: 'From visit' };

  const load = useCallback(() => {
    if (!studyId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/samples?study_id=${studyId}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/probe-weights`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/protocol-fields`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/sonda-measurements`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/extra-sondas`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/sonda-zones`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/weight-unit-config`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/sonda-observations`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/studies/${studyId}/sondas-order`, { withCredentials: true }).catch(() => ({ data: { order: [] } })),
    ]).then(([sr, vr, volr, wr, studyR, pfr, smr, exr, zr, ucr, sor, ordr]) => {
      setSamples(sr.data);
      // Filter out special visits (Valoración and Eventos Adversos) from probe tables
      setVisits((vr.data || []).filter(v => v.code !== 'VAL' && v.code !== 'EA'));
      // URS-F362: tras finalizar la ultima visita, el voluntario pasa a
      // 'completado' (F344). El estudio sigue activo y el voluntario debe
      // permanecer visible en todas las pestanas del estudio (incluido en
      // este panel de sondas/muestras) para preservar sus datos historicos.
      // URS-F394: incluir voluntarios excluidos/abandonos para que sus
      // mediciones y fotos ya cargadas sigan siendo visibles y editables
      // hasta la visita en la que fueron excluidos. Se muestra un badge
      // visual con la fecha en la columna del codigo (ver render).
      setVolunteers(sortByVCode(volr.data.filter(v => ['incluido', 'completado', 'excluido', 'abandono'].includes(v.status))));
      const wMap = {};
      const wMeta = {};
      for (const w of wr.data) {
        const u = w.unit || '1';
        const k = `${w.sample_id}|${w.volunteer_code}|${w.visit_code}|${u}`;
        wMap[k] = w.value;
        wMeta[k] = { saved_at: w.updated_at || '', initials: w.updated_by_initials || '' };
      }
      setWeights(wMap);
      setWeightMeta(wMeta);

      // Find sondas field and extract selected sondas
      const protocolData = studyR.data?.protocol_data || {};
      const sondasField = pfr.data.find(f => f.label === 'Sondas' && f.type === 'multiselect');
      if (sondasField) {
        const selectedSondas = protocolData[sondasField.field_id] || [];
        setSondas(Array.isArray(selectedSondas) ? selectedSondas : []);
      }
      // Detect ALL imaging devices selected in "Imagen" multiselect (URS-F217)
      const imagenField = pfr.data.find(f => (f.label || '').toLowerCase().startsWith('imagen') && f.type === 'multiselect');
      if (imagenField) {
        const imgs = protocolData[imagenField.field_id] || [];
        setImagingDevices(Array.isArray(imgs) ? imgs.filter(Boolean) : []);
      } else {
        setImagingDevices([]);
      }

      // Sonda measurements
      const smMap = {};
      for (const m of smr.data) {
        const key = `${m.sonda_name}|${m.volunteer_code}|${m.visit_code}`;
        smMap[key] = { value: m.value, saved_at: m.saved_at, initials: m.updated_by_initials || '' };
      }
      setSondaMeasurements(smMap);
      const valMap = {};
      for (const m of smr.data) {
        valMap[`${m.sonda_name}|${m.volunteer_code}|${m.visit_code}`] = m.value;
      }
      setSondaValues(valMap);
      setExtraSondas(exr.data.map(e => e.sonda_name));
      // Zones
      const zMap = {};
      for (const z of zr.data) {
        zMap[`${z.sonda_name}|${z.volunteer_code}`] = z.zone || '';
      }
      setSondaZones(zMap);
      // Unit config — migrate legacy
      const ucMap = {};
      for (const uc of ucr.data) {
        const cfg = uc.units_config || [{ unit: '1', from_visit: 'T0' }];
        if (uc.num_units >= 2 && cfg.length < uc.num_units) {
          // Legacy: had num_units=2 + unit2_from_visit
          while (cfg.length < uc.num_units) {
            cfg.push({ unit: String(cfg.length + 1), from_visit: uc.unit2_from_visit || 'T0' });
          }
        }
        ucMap[`${uc.sample_id}|${uc.volunteer_code}`] = { num_units: uc.num_units || cfg.length, units_config: cfg };
      }
      setUnitConfig(ucMap);

      // Per-sonda volunteer-level observations
      const obsMap = {};
      for (const o of (sor.data || [])) {
        obsMap[`${o.sonda_name}|${o.volunteer_code}`] = o.observation || '';
      }
      setSondaObservations(obsMap);
      setSondaOrder(Array.isArray(ordr.data?.order) ? ordr.data.order : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [studyId]);

  useEffect(() => { load(); }, [load]);

  // --- Sintomas Subjetivos Paciente: load whenever modal opens ---
  const loadSymptoms = useCallback(async () => {
    if (!studyId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/studies/${studyId}/subjective-symptoms`, { withCredentials: true });
      setSymptoms(data || []);
    } catch {
      toast.error(lang === 'es' ? 'Error cargando sintomas' : 'Error loading symptoms');
    }
  }, [studyId, lang]);

  useEffect(() => { if (studyId) loadSymptoms(); }, [studyId, loadSymptoms]);
  useEffect(() => { if (openSymptoms) loadSymptoms(); }, [openSymptoms, loadSymptoms]);

  const addSymptom = async () => {
    if (!newSymptom.volunteer_code || !newSymptom.visit_code || !newSymptom.observation.trim()) {
      toast.error(lang === 'es' ? 'Completa todos los campos' : 'Fill all fields');
      return;
    }
    setSavingSymptom(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/studies/${studyId}/subjective-symptoms`,
        newSymptom,
        { withCredentials: true },
      );
      setSymptoms(prev => [...prev, data]);
      setNewSymptom({ volunteer_code: '', visit_code: '', observation: '' });
      toast.success(lang === 'es' ? 'Sintoma anadido' : 'Symptom added');
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSavingSymptom(false);
    }
  };

  const startEditSymptom = (s) => {
    setEditingSymptomId(s.symptom_id);
    setEditingSymptomDraft({ volunteer_code: s.volunteer_code, visit_code: s.visit_code, observation: s.observation });
  };

  const saveEditSymptom = async () => {
    try {
      const { data } = await axios.put(
        `${API_URL}/api/studies/${studyId}/subjective-symptoms/${editingSymptomId}`,
        editingSymptomDraft,
        { withCredentials: true },
      );
      setSymptoms(prev => prev.map(x => x.symptom_id === editingSymptomId ? data : x));
      setEditingSymptomId(null);
      toast.success(lang === 'es' ? 'Sintoma actualizado' : 'Symptom updated');
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const deleteSymptom = async (sid) => {
    if (!window.confirm(lang === 'es' ? 'Eliminar este sintoma?' : 'Delete this symptom?')) return;
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/subjective-symptoms/${sid}`, { withCredentials: true });
      setSymptoms(prev => prev.filter(x => x.symptom_id !== sid));
      toast.success(lang === 'es' ? 'Sintoma eliminado' : 'Symptom deleted');
    } catch (err) {
      toast.error(errText(err));
    }
  };

  // --- Weight helpers ---
  const getWKey = (sampleId, volCode, visitCode, unit = '1') => `${sampleId}|${volCode}|${visitCode}|${unit}`;

  const handleWeightChange = (sampleId, volCode, visitCode, value, unit = '1') => {
    setWeights(prev => ({ ...prev, [getWKey(sampleId, volCode, visitCode, unit)]: value }));
  };

  const handleWeightKeyDown = (e, sampleId, volCode, visitCode, unit = '1') => {
    if (e.key === 'Enter') {
      const key = getWKey(sampleId, volCode, visitCode, unit);
      const val = weights[key];
      if (val && val.toLowerCase() === 'n') {
        setWeights(prev => ({ ...prev, [key]: 'No aplica' }));
      }
      saveWeight(sampleId, volCode, visitCode, unit);
    }
  };

  const saveWeight = async (sampleId, volCode, visitCode, unit = '1') => {
    const key = getWKey(sampleId, volCode, visitCode, unit);
    let value = weights[key];
    if (value && value.toLowerCase() === 'n') {
      value = 'No aplica';
      setWeights(prev => ({ ...prev, [key]: value }));
    }
    // For visits other than T0, weight cannot exceed T0 weight (same unit)
    if (value && value !== 'No aplica' && visitCode !== 'T0') {
      const t0Key = getWKey(sampleId, volCode, 'T0', unit);
      const t0Val = weights[t0Key];
      if (t0Val && t0Val !== 'No aplica') {
        const t0Float = parseFloat(t0Val);
        const valFloat = parseFloat(value);
        if (!isNaN(t0Float) && !isNaN(valFloat) && valFloat > t0Float) {
          toast.error(`${lb.error_exceed_t0} (${t0Float})`);
          return;
        }
      }
    }
    setSavingW(prev => ({ ...prev, [key]: true }));
    try {
      const { data } = await axios.put(`${API_URL}/api/studies/${studyId}/probe-weights`, {
        sample_id: sampleId, volunteer_code: volCode, visit_code: visitCode, value: value || null, unit,
      }, { withCredentials: true });
      setWeightMeta(prev => ({ ...prev, [key]: { saved_at: data.saved_at, initials: data.initials || '' } }));
      toast.success(lb.saving_ok);
    } catch (err) { toast.error(errText(err)); }
    setSavingW(prev => ({ ...prev, [key]: false }));
  };

  // --- Unit config helpers (N units) ---
  const getUCKey = (sampleId, volCode) => `${sampleId}|${volCode}`;

  const addUnit = async (sampleId, volCode) => {
    const ucKey = getUCKey(sampleId, volCode);
    const current = unitConfig[ucKey];
    const currentCfg = current?.units_config || [{ unit: '1', from_visit: 'T0' }];
    const newUnit = String(currentCfg.length + 1);
    const newCfg = [...currentCfg, { unit: newUnit, from_visit: 'T0' }];
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}/weight-unit-config`, {
        sample_id: sampleId, volunteer_code: volCode, num_units: newCfg.length, units_config: newCfg,
      }, { withCredentials: true });
      setUnitConfig(prev => ({ ...prev, [ucKey]: { num_units: newCfg.length, units_config: newCfg } }));
      toast.success(lang === 'es' ? `Unidad ${newUnit} añadida` : `Unit ${newUnit} added`);
    } catch (err) { toast.error(errText(err)); }
  };

  const removeLastUnit = async (sampleId, volCode) => {
    const ucKey = getUCKey(sampleId, volCode);
    const current = unitConfig[ucKey];
    const currentCfg = current?.units_config || [{ unit: '1', from_visit: 'T0' }];
    if (currentCfg.length <= 1) return;
    const newCfg = currentCfg.slice(0, -1);
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}/weight-unit-config`, {
        sample_id: sampleId, volunteer_code: volCode, num_units: newCfg.length, units_config: newCfg,
      }, { withCredentials: true });
      setUnitConfig(prev => ({ ...prev, [ucKey]: { num_units: newCfg.length, units_config: newCfg } }));
      toast.success(lang === 'es' ? 'Última unidad eliminada' : 'Last unit removed');
    } catch (err) { toast.error(errText(err)); }
  };

  const updateUnitFromVisit = async (sampleId, volCode, unitIdx, fromVisit) => {
    const ucKey = getUCKey(sampleId, volCode);
    const current = unitConfig[ucKey];
    const currentCfg = [...(current?.units_config || [{ unit: '1', from_visit: 'T0' }])];
    if (unitIdx < currentCfg.length) {
      currentCfg[unitIdx] = { ...currentCfg[unitIdx], from_visit: fromVisit };
    }
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}/weight-unit-config`, {
        sample_id: sampleId, volunteer_code: volCode, num_units: currentCfg.length, units_config: currentCfg,
      }, { withCredentials: true });
      setUnitConfig(prev => ({ ...prev, [ucKey]: { num_units: currentCfg.length, units_config: currentCfg } }));
    } catch (err) { toast.error(errText(err)); }
  };


  // --- Sonda helpers ---
  const getSKey = (sondaName, volCode, visitCode) => `${sondaName}|${volCode}|${visitCode}`;

  const handleSondaChange = (sondaName, volCode, visitCode, value) => {
    setSondaValues(prev => ({ ...prev, [getSKey(sondaName, volCode, visitCode)]: value }));
  };

  const handleSondaKeyDown = (e, sondaName, volCode, visitCode) => {
    if (e.key === 'Enter') {
      const key = getSKey(sondaName, volCode, visitCode);
      const val = sondaValues[key];
      if (val && val.toLowerCase() === 'n') {
        setSondaValues(prev => ({ ...prev, [key]: 'No aplica' }));
      }
      saveSonda(sondaName, volCode, visitCode);
    }
  };

  const saveSonda = async (sondaName, volCode, visitCode) => {
    const key = getSKey(sondaName, volCode, visitCode);
    let value = sondaValues[key];
    if (value && value.toLowerCase() === 'n') {
      value = 'No aplica';
      setSondaValues(prev => ({ ...prev, [key]: value }));
    }
    setSavingS(prev => ({ ...prev, [key]: true }));
    try {
      const { data } = await axios.put(`${API_URL}/api/studies/${studyId}/sonda-measurements`, {
        sonda_name: sondaName, volunteer_code: volCode, visit_code: visitCode, value: value || null,
      }, { withCredentials: true });
      setSondaMeasurements(prev => ({ ...prev, [key]: { value, saved_at: data.saved_at, initials: data.initials || '' } }));
      toast.success(lb.sonda_saved);
    } catch (err) { toast.error(errText(err)); }
    setSavingS(prev => ({ ...prev, [key]: false }));
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const getZoneKey = (sondaName, volCode) => `${sondaName}|${volCode}`;

  const handleZoneChange = (sondaName, volCode, value) => {
    setSondaZones(prev => ({ ...prev, [getZoneKey(sondaName, volCode)]: value }));
  };

  const saveVolunteerZone = async (sondaName, volCode) => {
    const zone = sondaZones[getZoneKey(sondaName, volCode)] || '';
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}/sonda-zones`, { sonda_name: sondaName, volunteer_code: volCode, zone }, { withCredentials: true });
    } catch (err) { toast.error(errText(err)); }
  };

  const saveHeaderZoneAndApply = async (sondaName) => {
    setSavingZone(true);
    try {
      const volCodes = volunteers.map(v => v.study_volunteer_code);
      await axios.put(`${API_URL}/api/studies/${studyId}/sonda-zones/bulk`, { sonda_name: sondaName, zone: headerZone, volunteer_codes: volCodes }, { withCredentials: true });
      // Update local state
      const newZones = { ...sondaZones, [getZoneKey(sondaName, '')]: headerZone };
      volCodes.forEach(vc => { newZones[getZoneKey(sondaName, vc)] = headerZone; });
      setSondaZones(newZones);
      toast.success(lb.zone_saved);
    } catch (err) { toast.error(errText(err)); }
    setSavingZone(false);
  };

  // --- Per-volunteer observations on each probe ---
  const getObsKey = (sondaName, volCode) => `${sondaName}|${volCode}`;
  const handleObsChange = (sondaName, volCode, value) => {
    setSondaObservations(prev => ({ ...prev, [getObsKey(sondaName, volCode)]: value }));
  };
  const saveObservation = async (sondaName, volCode) => {
    const key = getObsKey(sondaName, volCode);
    setSavingObs(prev => ({ ...prev, [key]: true }));
    try {
      await axios.put(
        `${API_URL}/api/studies/${studyId}/sonda-observations`,
        { sonda_name: sondaName, volunteer_code: volCode, observation: sondaObservations[key] || '' },
        { withCredentials: true },
      );
    } catch (err) { toast.error(errText(err)); }
    setSavingObs(prev => ({ ...prev, [key]: false }));
  };

  // --- Dispense flow inline (replicates Muestras flow) ---
  const [allVisits, setAllVisits] = useState([]);
  const handleOpenDispense = async (sample, e) => {
    if (e) e.stopPropagation();
    setOpenDispense(sample);
    setDispForm({ volunteer_id: '', visit_code: '', quantity: 1, date: new Date().toISOString().split('T')[0], notes: '' });
    try {
      const vr = await axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true });
      setAllVisits(vr.data || []);
    } catch {
      setAllVisits(visits);
    }
  };
  const handleConfirmDispense = async () => {
    if (!dispForm.volunteer_id) {
      toast.error(lang === 'es' ? 'Selecciona un voluntario' : 'Select a volunteer');
      return;
    }
    setSavingDispense(true);
    try {
      await axios.post(`${API_URL}/api/dispensations`, { sample_id: openDispense.sample_id, ...dispForm }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Muestra dispensada correctamente' : 'Sample dispensed successfully');
      setOpenDispense(null);
      // Refresh samples to update stock_remaining
      const sr = await axios.get(`${API_URL}/api/samples?study_id=${studyId}`, { withCredentials: true });
      setSamples(sr.data);
    } catch (err) {
      toast.error(errText(err));
    }
    setSavingDispense(false);
  };

  if (!studyId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el estudio primero' : 'Save the study first'}</p>;
  if (loading) return <div className="p-12 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>;

  const currentWeightSample = openWeight ? samples.find(s => s.sample_id === openWeight) : null;
  const isUnitMode = (currentWeightSample?.measure_mode || 'peso') === 'unidades';
  const initialWeight = currentWeightSample?.unit_weight ? parseFloat(currentWeightSample.unit_weight) : null;
  const allSondas = (() => {
    // URS-F217: one "Fotografias - {device}" auto-sonda per imaging device in protocol
    const photoSondas = (imagingDevices || []).map(d => `Fotografias - ${d}`).filter(name => !sondas.includes(name) && !extraSondas.includes(name));
    const raw = [
      ...sondas,
      ...extraSondas,
      ...photoSondas,
    ];
    if (!sondaOrder || sondaOrder.length === 0) return raw;
    const set = new Set(raw);
    const ordered = sondaOrder.filter(n => set.has(n));
    const rest = raw.filter(n => !ordered.includes(n));
    return [...ordered, ...rest];
  })();

  // Persist new order to backend (best-effort, optimistic UI)
  const persistOrder = async (next) => {
    setSondaOrder(next);
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}/sondas-order`, { order: next }, { withCredentials: true });
    } catch {
      toast.error(lang === 'es' ? 'No se pudo guardar el orden' : 'Could not save order');
    }
  };

  const reorderSonda = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= allSondas.length || to >= allSondas.length) return;
    const next = [...allSondas];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persistOrder(next);
  };
  // Sintomas Subjetivos Paciente: always available, regardless of probes/samples.
  const hasEntries = true;

  const handleDuplicate = async (baseName) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/studies/${studyId}/extra-sondas`, { base_name: baseName }, { withCredentials: true });
      setExtraSondas(prev => [...prev, data.sonda_name]);
      toast.success(`${lb.duplicated}: ${data.sonda_name}`);
    } catch (err) { toast.error(errText(err)); }
  };

  const handleDeleteExtra = async (sondaName) => {
    setDeletingSonda(true);
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/extra-sondas/${encodeURIComponent(sondaName)}`, { withCredentials: true });
      setExtraSondas(prev => prev.filter(n => n !== sondaName));
      setConfirmDeleteSonda(null);
      toast.success(lb.deleted);
    } catch (err) { toast.error(errText(err)); }
    setDeletingSonda(false);
  };

  if (!hasEntries) {
    return (
      <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
        <Scales size={40} className="text-stone-300 mx-auto mb-3" />
        <p className="text-sm text-stone-500">{lb.no_entries}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="probes-panel">
      {/* Sintomas Subjetivos Paciente — always present in every study */}
      <div
        className="bg-violet-50/40 border border-violet-200 rounded-sm px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-violet-50 transition-colors"
        data-testid="symptoms-card"
        onClick={() => setOpenSymptoms(true)}
      >
        <div className="flex items-center gap-3">
          <ChatCircleDots size={16} className="text-violet-700" />
          <span className="text-sm font-semibold text-stone-900">{lang === 'es' ? 'Sintomas Subjetivos Paciente' : 'Patient Subjective Symptoms'}</span>
          <Badge variant="outline" className="text-[10px] bg-violet-100 text-violet-800 border-violet-200">{symptoms.length}</Badge>
        </div>
        <span className="text-xs text-stone-500">{lang === 'es' ? 'Abrir tabla' : 'Open table'}</span>
      </div>

      {/* Weight entries from samples (always first) */}
      {samples.map(sample => {
        const isUnits = (sample.measure_mode || 'peso') === 'unidades';
        const measureLabel = isUnits ? (lang === 'es' ? 'Unidades' : 'Units') : lb.weights;
        const initialLabel = isUnits ? lb.initial_qty : lb.initial_weight;
        const initialSuffix = isUnits ? ' uds' : ' g';
        return (
        <div
          key={sample.sample_id}
          className="bg-white border border-stone-200 rounded-sm px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-stone-50 transition-colors"
          data-testid={`probe-sample-${sample.sample_id}`}
          onClick={() => setOpenWeight(sample.sample_id)}
        >
          <div className="flex items-center gap-3">
            <Scales size={16} className="text-[#1f3e80]" />
            <span className="text-sm font-semibold text-stone-900">{measureLabel} {sample.product_name}</span>
            <Badge variant="outline" className="text-[10px] font-mono">{sample.sample_id}</Badge>
            {isUnits && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{lang === 'es' ? 'Conteo' : 'Count'}</Badge>}
          </div>
          {sample.unit_weight && (
            <Badge variant="outline" className={`text-xs ${isUnits ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
              {initialLabel}: {sample.unit_weight}{initialSuffix}
            </Badge>
          )}
        </div>
        );
      })}

      {/* Sonda entries from protocol + duplicated */}
      {allSondas.map((sondaName, idx) => {
        const isExtra = extraSondas.includes(sondaName);
        const isPhoto = sondaName.startsWith('Fotografias');
        const baseName = isExtra ? sondaName : sondaName;
        const isDragging = dragIdx === idx;
        const isDragOver = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;
        return (
          <div
            key={sondaName}
            className={`bg-white border rounded-sm px-3 py-3 flex items-center justify-between transition-all ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'border-[#1f3e80] ring-1 ring-[#1f3e80]/40 bg-[#1f3e80]/5' : 'border-stone-200'}`}
            data-testid={`sonda-entry-${sondaName}`}
            draggable
            onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(idx)); } catch {} }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverIdx !== idx) setDragOverIdx(idx); }}
            onDragLeave={() => { if (dragOverIdx === idx) setDragOverIdx(null); }}
            onDrop={(e) => {
              e.preventDefault();
              const fromStr = e.dataTransfer.getData('text/plain');
              const from = fromStr ? parseInt(fromStr, 10) : dragIdx;
              if (from != null && !isNaN(from)) reorderSonda(from, idx);
              setDragIdx(null); setDragOverIdx(null);
            }}
            onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
          >
            <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 flex-1" onClick={() => { setOpenSonda(sondaName); setHeaderZone(sondaZones[getZoneKey(sondaName, '')] || ''); }}>
              <span
                className="shrink-0 text-stone-400 hover:text-stone-700 cursor-grab active:cursor-grabbing px-1"
                title={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}
                data-testid={`sonda-drag-handle-${sondaName}`}
                onClick={(e) => e.stopPropagation()}
              >
                <DotsSixVertical size={16} weight="bold" />
              </span>
              {isPhoto ? <Camera size={16} className="text-fuchsia-600" /> : <Thermometer size={16} className="text-indigo-600" />}
              <span className="text-sm font-semibold text-stone-900">{sondaName}</span>
              <Badge variant="outline" className={`text-[10px] ${isPhoto ? 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
                {isPhoto ? (lang === 'es' ? 'Imagen' : 'Image') : (lang === 'es' ? 'Sonda' : 'Probe')}
              </Badge>
              {isPhoto && sondaZones[getZoneKey(sondaName, '')] && (
                <Badge variant="outline" data-testid={`sonda-zone-badge-${sondaName}`}
                  className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                  {lb.zone}: {sondaZones[getZoneKey(sondaName, '')]}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button variant="outline" size="sm" data-testid={`sonda-duplicate-${sondaName}`}
                className={`h-7 px-2.5 text-xs gap-1.5 ${isPhoto ? 'text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50' : 'text-indigo-700 border-indigo-200 hover:bg-indigo-50'}`}
                onClick={(e) => { e.stopPropagation(); handleDuplicate(isExtra ? baseName.replace(/ \(Zona \d+\)$/, '') : sondaName); }}>
                <CopySimple size={13} />
                {lb.duplicate}
              </Button>
              {isExtra && isAdmin && (
                <Button variant="ghost" size="icon" data-testid={`sonda-delete-${sondaName}`}
                  className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteSonda(sondaName); }}>
                  <Trash size={13} />
                </Button>
              )}
            </div>
          </div>
        );
      })}

      {/* ===== SONDA DIALOG ===== */}
      <Dialog open={!!openSonda} onOpenChange={v => { if (!v) setOpenSonda(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {openSonda && openSonda.startsWith('Fotografias') ? <Camera size={20} className="text-fuchsia-600" /> : <Thermometer size={20} className="text-indigo-600" />}
              {openSonda}
              {openSonda && openSonda.startsWith('Fotografias') && sondaZones[getZoneKey(openSonda, '')] && (
                <Badge variant="outline" className="text-[11px] bg-amber-50 text-amber-700 border-amber-200">
                  {lb.zone}: {sondaZones[getZoneKey(openSonda, '')]}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>{studyTitle} | {openSonda}</DialogDescription>
          </DialogHeader>
          {(!openSonda || !openSonda.startsWith('Fotografias')) && (
            <div className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-sm px-4 py-2.5">
              <span className="text-xs font-semibold text-stone-600 shrink-0">{lb.zone}:</span>
              <Input
                data-testid="sonda-header-zone"
                value={headerZone}
                onChange={e => setHeaderZone(e.target.value)}
                placeholder={lb.zone_placeholder}
                className="h-8 text-xs flex-1 max-w-xs"
              />
              <Button variant="outline" size="sm" data-testid="sonda-zone-apply-btn"
                className="h-8 px-3 text-xs gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                disabled={savingZone}
                onClick={() => openSonda && saveHeaderZoneAndApply(openSonda)}>
                <FloppyDisk size={13} />
                {savingZone ? '...' : lb.zone_save}
              </Button>
            </div>
          )}
          {openSonda && openSonda.startsWith('Fotografias') && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 bg-fuchsia-50 border border-fuchsia-200 rounded-sm px-4 py-2.5">
                <span className="text-xs font-semibold text-fuchsia-800 shrink-0">{lb.zone}:</span>
                <Input
                  data-testid="sonda-header-zone-photo"
                  value={headerZone}
                  onChange={e => setHeaderZone(e.target.value)}
                  placeholder={lang === 'es' ? 'Ej: Rostro, Espalda, Antebrazo derecho...' : 'E.g.: Face, Back, Right forearm...'}
                  className="h-8 text-xs flex-1 max-w-xs bg-white"
                />
                <Button variant="outline" size="sm" data-testid="sonda-zone-apply-photo-btn"
                  className="h-8 px-3 text-xs gap-1.5 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-100 bg-white"
                  disabled={savingZone}
                  onClick={() => openSonda && saveHeaderZoneAndApply(openSonda)}>
                  <FloppyDisk size={13} />
                  {savingZone ? '...' : lb.zone_save}
                </Button>
              </div>
              <div className="flex items-center gap-3 bg-fuchsia-50/60 border border-fuchsia-200 rounded-sm px-4 py-2.5">
                <div className="text-xs text-fuchsia-800 flex-1">
                  <b>{lang === 'es' ? 'Importacion masiva por carpeta:' : 'Bulk import by folder:'}</b>{' '}
                  {lang === 'es' ? 'estructura esperada' : 'expected structure'} <code className="bg-white px-1 rounded border border-fuchsia-200">{openSonda.replace('Fotografias - ', '')}/V1/T0/foto.jpg</code> {lang === 'es' ? '(o cualquier carpeta raiz)' : '(any root folder)'}
                </div>
                <PhotoBulkImport
                  studyId={studyId}
                  device={openSonda.replace('Fotografias - ', '')}
                  volunteers={volunteers}
                  visits={visits}
                  onDone={() => loadPhotos(openSonda)}
                  lang={lang}
                />
              </div>
            </div>
          )}
          <div className="flex-1 overflow-auto">
            {volunteers.length === 0 ? (
              <p className="text-sm text-stone-400 py-8 text-center">{lb.no_volunteers}</p>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-600 sticky left-0 bg-stone-50 z-10 min-w-[70px]">V-Code</th>
                    {(!openSonda || !openSonda.startsWith('Fotografias')) && (
                      <th className="text-center px-1 py-2 text-xs font-semibold text-stone-600 min-w-[130px]">{lb.zone}</th>
                    )}
                    {visits.map(v => (
                      <th key={v.visit_id} className="text-center px-1 py-2 text-xs font-semibold text-stone-600 min-w-[140px]">{v.code}</th>
                    ))}
                    {(!openSonda || !openSonda.startsWith('Fotografias')) && (
                      <th className="text-center px-1 py-2 text-xs font-semibold text-stone-600 min-w-[200px]">{lang === 'es' ? 'Observaciones' : 'Observations'}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {volunteers.map(vol => (
                    <tr key={vol.volunteer_id} className="border-b border-stone-100" data-testid={`sonda-row-${vol.study_volunteer_code}`}>
                      <td className="px-3 py-1.5 font-mono text-sm font-bold text-[#1f3e80] sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-1.5">
                          <span>{vol.study_volunteer_code}</span>
                          {/* URS-F394: badge de estado terminal para diferenciar
                              a los voluntarios excluidos/abandonos. Se muestra
                              solo cuando su status ya no es incluido/completado. */}
                          {(vol.status === 'excluido' || vol.status === 'abandono') && (
                            <span
                              data-testid={`sonda-vol-status-${vol.study_volunteer_code}`}
                              title={`${vol.status === 'excluido' ? (lang === 'es' ? 'Excluido' : 'Excluded') : (lang === 'es' ? 'Abandono' : 'Withdrawal')}${vol.exclusion_date ? ` — ${(vol.exclusion_date || '').substring(0, 10)}` : ''}${vol.exclusion_reason ? `: ${vol.exclusion_reason}` : ''}`}
                              className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-sm border ${vol.status === 'excluido' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                            >
                              {vol.status === 'excluido'
                                ? (lang === 'es' ? 'EXC' : 'EXC')
                                : (lang === 'es' ? 'AB' : 'WD')}
                            </span>
                          )}
                        </div>
                      </td>
                      {(!openSonda || !openSonda.startsWith('Fotografias')) && (
                        <td className="px-1 py-1.5">
                          <div className="flex items-center gap-1">
                            <Input
                              data-testid={`sonda-zone-${vol.study_volunteer_code}`}
                              value={sondaZones[getZoneKey(openSonda, vol.study_volunteer_code)] || ''}
                              onChange={e => handleZoneChange(openSonda, vol.study_volunteer_code, e.target.value)}
                              onBlur={() => saveVolunteerZone(openSonda, vol.study_volunteer_code)}
                              className="h-8 text-xs text-center border-stone-200"
                              placeholder={lb.zone}
                            />
                          </div>
                        </td>
                      )}
                      {visits.map(v => {
                        const key = getSKey(openSonda, vol.study_volunteer_code, v.code);
                        const val = sondaValues[key] || '';
                        const measurement = sondaMeasurements[key];
                        const isSaving = savingS[key];
                        const photoSonda = openSonda && openSonda.startsWith('Fotografias');
                        if (photoSonda) {
                          const device = openSonda.replace('Fotografias - ', '');
                          const cellPhotos = (volunteerPhotos[openSonda] || []).filter(p => p.volunteer_code === vol.study_volunteer_code && p.visit_code === v.code);
                          return (
                            <td key={v.visit_id} className="px-1 py-1.5">
                              <PhotoCell
                                studyId={studyId}
                                volunteerCode={vol.study_volunteer_code}
                                visitCode={v.code}
                                device={device}
                                photos={cellPhotos}
                                onChanged={() => loadPhotos(openSonda)}
                                lang={lang}
                              />
                            </td>
                          );
                        }
                        return (
                          <td key={v.visit_id} className="px-1 py-1.5">
                            <div className="flex items-center gap-1">
                              <div className="flex-1">
                                <Input
                                  data-testid={`sonda-val-${vol.study_volunteer_code}-${v.code}`}
                                  type="text"
                                  value={val}
                                  onChange={e => handleSondaChange(openSonda, vol.study_volunteer_code, v.code, e.target.value)}
                                  onKeyDown={e => handleSondaKeyDown(e, openSonda, vol.study_volunteer_code, v.code)}
                                  className={`h-8 text-center text-xs ${val === 'No aplica' ? 'bg-stone-100 text-stone-500 italic' : 'border-stone-200'}`}
                                />
                                {measurement?.saved_at && (
                                  <p className="text-[9px] text-stone-400 text-center mt-0.5 leading-tight">{formatDateTime(measurement.saved_at)}{measurement.initials ? ` · ${measurement.initials}` : ''}</p>
                                )}
                              </div>
                              <Button
                                variant="ghost" size="icon"
                                className="h-8 w-8 shrink-0 text-stone-400 hover:text-indigo-600"
                                data-testid={`sonda-save-${vol.study_volunteer_code}-${v.code}`}
                                onClick={() => saveSonda(openSonda, vol.study_volunteer_code, v.code)}
                                disabled={isSaving}
                              >
                                <FloppyDisk size={14} />
                              </Button>
                            </div>
                          </td>
                        );
                      })}
                      {/* Observaciones por voluntario (oculto para Fotografias) */}
                      {(!openSonda || !openSonda.startsWith('Fotografias')) && (
                        <td className="px-1 py-1.5">
                          <Textarea
                            data-testid={`sonda-obs-${vol.study_volunteer_code}`}
                            rows={2}
                            value={sondaObservations[getObsKey(openSonda, vol.study_volunteer_code)] || ''}
                            onChange={e => handleObsChange(openSonda, vol.study_volunteer_code, e.target.value)}
                            onBlur={() => saveObservation(openSonda, vol.study_volunteer_code)}
                            placeholder={lang === 'es' ? 'Anotaciones del voluntario...' : 'Volunteer notes...'}
                            className="text-xs border-stone-200 resize-y min-h-[42px]"
                            disabled={!!savingObs[getObsKey(openSonda, vol.study_volunteer_code)]}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== WEIGHTS DIALOG ===== */}
      <Dialog open={!!openWeight} onOpenChange={v => { if (!v) setOpenWeight(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scales size={20} className="text-[#1f3e80]" />
              {isUnitMode ? (lang === 'es' ? 'Unidades' : 'Units') : lb.weights} {currentWeightSample?.product_name}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 flex-wrap">
              <span>{studyTitle}</span>
              <span className="text-stone-300">|</span>
              <span>{currentWeightSample?.product_name}</span>
              {initialWeight != null && (
                <>
                  <span className="text-stone-300">|</span>
                  <span className={`font-medium ${isUnitMode ? 'text-amber-700' : 'text-blue-700'}`}>
                    {isUnitMode ? lb.initial_qty : lb.initial_weight}: {initialWeight}{isUnitMode ? ' uds' : ' g'}
                  </span>
                </>
              )}
              {isUnitMode && (
                <>
                  <span className="text-stone-300">|</span>
                  <span className="text-amber-600 text-xs">{lang === 'es' ? 'Anotar unidades restantes en cada visita' : 'Record remaining units per visit'}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {currentWeightSample && (
            <div className="flex items-center justify-end pb-2 -mt-1">
              <Button
                variant="outline" size="sm"
                data-testid={`probe-dispense-${currentWeightSample.sample_id}`}
                className="h-8 px-3 text-xs gap-1.5 text-green-700 border-green-200 hover:bg-green-50"
                disabled={currentWeightSample.stock_remaining <= 0}
                title={currentWeightSample.stock_remaining <= 0 ? (lang === 'es' ? 'Sin stock disponible' : 'Out of stock') : ''}
                onClick={() => handleOpenDispense(currentWeightSample)}
              >
                <Package size={14} />
                {lang === 'es' ? 'Dispensar' : 'Dispense'}
              </Button>
            </div>
          )}
          <div className="flex-1 overflow-auto">
            {volunteers.length === 0 ? (
              <p className="text-sm text-stone-400 py-8 text-center">{lb.no_volunteers}</p>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-600 sticky left-0 bg-stone-50 z-10 min-w-[120px]">V-Code</th>
                    {visits.map(v => (
                      <th key={v.visit_id} className="text-center px-1 py-2 text-xs font-semibold text-stone-600 min-w-[120px]">{v.code}</th>
                    ))}
                    <th className="text-center px-2 py-2 text-xs font-semibold text-stone-600 min-w-[90px]">{lang === 'es' ? 'Unidades' : 'Units'}</th>
                  </tr>
                </thead>
                <tbody>
                  {volunteers.map(vol => {
                    const ucKey = getUCKey(currentWeightSample?.sample_id, vol.study_volunteer_code);
                    const uc = unitConfig[ucKey];
                    const unitsCfg = uc?.units_config || [{ unit: '1', from_visit: 'T0' }];
                    const numUnits = unitsCfg.length;
                    const visitCodes = visits.map(v => v.code);

                    const renderWeightRow = (unitCfg, unitIdx) => {
                      const unit = unitCfg.unit;
                      const fromVisit = unitCfg.from_visit || 'T0';
                      const fromIdx = visitCodes.indexOf(fromVisit);
                      const isExtra = unitIdx > 0;
                      const bgColors = ['', 'bg-blue-50/30', 'bg-emerald-50/30', 'bg-amber-50/30', 'bg-purple-50/30', 'bg-pink-50/30'];
                      const bgColor = bgColors[unitIdx % bgColors.length] || 'bg-stone-50/30';
                      const sampleCode = currentWeightSample?.sample_id || '';
                      const label = numUnits > 1
                        ? `${vol.study_volunteer_code} - ${sampleCode} (${unit})`
                        : vol.study_volunteer_code;
                      return (
                        <tr key={`${vol.volunteer_id}-u${unit}`} className={`border-b border-stone-100 ${isExtra ? bgColor : ''}`} data-testid={`probe-row-${vol.study_volunteer_code}-u${unit}`}>
                          <td className={`px-3 py-1.5 font-mono text-sm sticky left-0 z-10 ${isExtra ? `${bgColor} pl-6 text-stone-600 font-medium` : 'bg-white font-bold text-[#1f3e80]'}`}>
                            {label}
                            {isExtra && (
                              <select
                                className="ml-2 text-[10px] bg-white border border-stone-200 rounded px-1 py-0.5 text-stone-500"
                                value={fromVisit}
                                onChange={e => updateUnitFromVisit(currentWeightSample?.sample_id, vol.study_volunteer_code, unitIdx, e.target.value)}
                                data-testid={`unit-from-${vol.study_volunteer_code}-u${unit}`}
                                title={lb.unit2_from}
                              >
                                {visits.map(v => <option key={v.visit_id} value={v.code}>{v.code}</option>)}
                              </select>
                            )}
                          </td>
                          {visits.map((v, vIdx) => {
                            const disabled = isExtra && fromIdx > vIdx;
                            const key = getWKey(currentWeightSample?.sample_id, vol.study_volunteer_code, v.code, unit);
                            const val = disabled ? '' : (weights[key] || '');
                            const isSaving = savingW[key];
                            const meta = weightMeta[key];
                            const t0Key = getWKey(currentWeightSample?.sample_id, vol.study_volunteer_code, 'T0', unit);
                            const t0Val = weights[t0Key];
                            const t0Weight = (t0Val && t0Val !== 'No aplica') ? parseFloat(t0Val) : null;
                            const exceeds = v.code !== 'T0' && val && val !== 'No aplica' && t0Weight != null && !isNaN(parseFloat(val)) && parseFloat(val) > t0Weight;
                            return (
                              <td key={v.visit_id} className="px-1 py-1.5">
                                {disabled ? (
                                  <div className="h-8 bg-stone-100 rounded-sm" title={lang === 'es' ? 'No aplica en esta visita' : 'N/A for this visit'} />
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <div className="flex-1">
                                      <Input
                                        data-testid={`probe-weight-${vol.study_volunteer_code}-${v.code}-u${unit}`}
                                        type="text"
                                        value={val}
                                        onChange={e => handleWeightChange(currentWeightSample?.sample_id, vol.study_volunteer_code, v.code, e.target.value, unit)}
                                        onKeyDown={e => handleWeightKeyDown(e, currentWeightSample?.sample_id, vol.study_volunteer_code, v.code, unit)}
                                        className={`h-8 text-center text-xs ${val === 'No aplica' ? 'bg-stone-100 text-stone-500 italic' : exceeds ? 'border-red-400 bg-red-50 text-red-700' : 'border-stone-200'}`}
                                      />
                                      {meta?.saved_at && (
                                        <p className="text-[9px] text-stone-400 text-center mt-0.5 leading-tight">{formatDateTime(meta.saved_at)}{meta.initials ? ` · ${meta.initials}` : ''}</p>
                                      )}
                                    </div>
                                    <Button
                                      variant="ghost" size="icon"
                                      className="h-8 w-8 shrink-0 text-stone-400 hover:text-[#1f3e80]"
                                      data-testid={`probe-save-${vol.study_volunteer_code}-${v.code}-u${unit}`}
                                      onClick={() => saveWeight(currentWeightSample?.sample_id, vol.study_volunteer_code, v.code, unit)}
                                      disabled={isSaving}
                                    >
                                      <FloppyDisk size={14} />
                                    </Button>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          {unitIdx === 0 && (
                            <td rowSpan={numUnits} className="text-center px-1 py-1.5 align-top">
                              <div className="flex flex-col gap-1 items-center">
                                <Button
                                  variant="ghost" size="sm"
                                  className="text-[10px] h-7 text-blue-600 hover:text-blue-800"
                                  data-testid={`add-unit-${vol.study_volunteer_code}`}
                                  onClick={() => addUnit(currentWeightSample?.sample_id, vol.study_volunteer_code)}
                                >
                                  + {lang === 'es' ? 'Unidad' : 'Unit'}
                                </Button>
                                {numUnits > 1 && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="text-[10px] h-7 text-red-500 hover:text-red-700"
                                    data-testid={`remove-unit-${vol.study_volunteer_code}`}
                                    onClick={() => removeLastUnit(currentWeightSample?.sample_id, vol.study_volunteer_code)}
                                  >
                                    - {lang === 'es' ? 'Quitar' : 'Remove'}
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    };

                    return (
                      <React.Fragment key={vol.volunteer_id}>
                        {unitsCfg.map((ucfg, idx) => renderWeightRow(ucfg, idx))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!confirmDeleteSonda}
        onOpenChange={(v) => { if (!v) setConfirmDeleteSonda(null); }}
        title={lang === 'es' ? 'Eliminar sonda' : 'Delete probe'}
        description={lang === 'es' ? `¿Estás seguro de que deseas eliminar la sonda "${confirmDeleteSonda || ''}"?` : `Are you sure you want to delete probe "${confirmDeleteSonda || ''}"?`}
        onConfirm={() => handleDeleteExtra(confirmDeleteSonda)}
        loading={deletingSonda}
      />

      {/* ===== SINTOMAS SUBJETIVOS PACIENTE DIALOG ===== */}
      <Dialog open={openSymptoms} onOpenChange={v => { if (!v) setOpenSymptoms(false); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChatCircleDots size={20} className="text-violet-700" />
              {lang === 'es' ? 'Sintomas Subjetivos Paciente' : 'Patient Subjective Symptoms'}
            </DialogTitle>
            <DialogDescription>{studyTitle}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-3">
            {/* Existing rows */}
            <div className="border border-stone-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50">
                  <tr className="text-left text-stone-600">
                    <th className="px-3 py-2 text-xs font-semibold w-[110px]">{lang === 'es' ? 'Voluntario' : 'Volunteer'}</th>
                    <th className="px-3 py-2 text-xs font-semibold w-[110px]">{lang === 'es' ? 'Visita' : 'Visit'}</th>
                    <th className="px-3 py-2 text-xs font-semibold">{lang === 'es' ? 'Observacion' : 'Observation'}</th>
                    <th className="px-3 py-2 text-xs font-semibold w-[90px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {symptoms.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-stone-400">{lang === 'es' ? 'Aun no hay sintomas registrados.' : 'No symptoms recorded yet.'}</td></tr>
                  )}
                  {symptoms.map(s => {
                    const isEditing = editingSymptomId === s.symptom_id;
                    const draft = isEditing ? editingSymptomDraft : s;
                    return (
                      <tr key={s.symptom_id} className="border-t border-stone-100" data-testid={`symptom-row-${s.symptom_id}`}>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <select
                              value={draft.volunteer_code}
                              onChange={e => setEditingSymptomDraft(p => ({ ...p, volunteer_code: e.target.value }))}
                              className="border border-stone-200 rounded px-2 py-1 text-xs w-full"
                            >
                              {volunteers.map(v => (
                                <option key={v.study_volunteer_code} value={v.study_volunteer_code}>{v.study_volunteer_code} - {v.volunteer_name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="font-mono text-xs font-bold text-[#1f3e80]">{s.volunteer_code}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <select
                              value={draft.visit_code}
                              onChange={e => setEditingSymptomDraft(p => ({ ...p, visit_code: e.target.value }))}
                              className="border border-stone-200 rounded px-2 py-1 text-xs w-full"
                            >
                              {[{ code: 'VAL', name: 'Valoracion' }, ...visits].map(v => (
                                <option key={v.code} value={v.code}>{v.code}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-xs font-mono">{s.visit_code}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <textarea
                              value={draft.observation}
                              onChange={e => setEditingSymptomDraft(p => ({ ...p, observation: e.target.value }))}
                              rows={2}
                              className="w-full text-xs border border-stone-200 rounded px-2 py-1"
                            />
                          ) : (
                            <span className="text-xs text-stone-800 whitespace-pre-wrap">{s.observation}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {isEditing ? (
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" onClick={saveEditSymptom} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">{lang === 'es' ? 'Guardar' : 'Save'}</Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingSymptomId(null)} className="h-7 text-xs">{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                            </div>
                          ) : (
                            <div className="flex gap-1 justify-end">
                              <Button size="icon" variant="ghost" onClick={() => startEditSymptom(s)} className="h-7 w-7 text-stone-500 hover:text-indigo-600" data-testid={`symptom-edit-${s.symptom_id}`}>
                                <FloppyDisk size={14} />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => deleteSymptom(s.symptom_id)} className="h-7 w-7 text-stone-400 hover:text-red-600" data-testid={`symptom-delete-${s.symptom_id}`}>
                                <Trash size={14} />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Add new symptom */}
            <div className="border border-violet-200 bg-violet-50/40 rounded p-3 space-y-2" data-testid="symptom-new-form">
              <p className="text-xs font-semibold text-violet-900">{lang === 'es' ? 'Anadir nuevo sintoma' : 'Add new symptom'}</p>
              <div className="grid grid-cols-1 md:grid-cols-[120px_120px_1fr_auto] gap-2 items-start">
                <select
                  value={newSymptom.volunteer_code}
                  onChange={e => setNewSymptom(p => ({ ...p, volunteer_code: e.target.value }))}
                  className="border border-stone-200 rounded px-2 py-1.5 text-xs h-9"
                  data-testid="symptom-new-volunteer"
                >
                  <option value="">{lang === 'es' ? 'Voluntario...' : 'Volunteer...'}</option>
                  {volunteers.map(v => (
                    <option key={v.study_volunteer_code} value={v.study_volunteer_code}>{v.study_volunteer_code} - {v.volunteer_name}</option>
                  ))}
                </select>
                <select
                  value={newSymptom.visit_code}
                  onChange={e => setNewSymptom(p => ({ ...p, visit_code: e.target.value }))}
                  className="border border-stone-200 rounded px-2 py-1.5 text-xs h-9"
                  data-testid="symptom-new-visit"
                >
                  <option value="">{lang === 'es' ? 'Visita...' : 'Visit...'}</option>
                  {[{ code: 'VAL', name: 'Valoracion' }, ...visits].map(v => (
                    <option key={v.code} value={v.code}>{v.code}</option>
                  ))}
                </select>
                <textarea
                  value={newSymptom.observation}
                  onChange={e => setNewSymptom(p => ({ ...p, observation: e.target.value }))}
                  placeholder={lang === 'es' ? 'Observacion del paciente...' : 'Patient observation...'}
                  rows={1}
                  className="border border-stone-200 rounded px-2 py-1.5 text-xs"
                  data-testid="symptom-new-observation"
                />
                <Button
                  onClick={addSymptom}
                  disabled={savingSymptom}
                  className="h-9 bg-violet-700 hover:bg-violet-800 text-white text-xs gap-1.5"
                  data-testid="symptom-new-add-btn"
                >
                  <Plus size={14} />
                  {lang === 'es' ? 'Anadir' : 'Add'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DISPENSE DIALOG (inline replica of Muestras flow) ===== */}
      <Dialog open={!!openDispense} onOpenChange={(v) => { if (!v) setOpenDispense(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={20} className="text-green-700" />
              {lang === 'es' ? 'Dispensar' : 'Dispense'}: {openDispense?.product_name}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {openDispense?.sample_id} — {lang === 'es' ? 'Restante' : 'Remaining'}: {openDispense?.stock_remaining}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{lang === 'es' ? 'Voluntario' : 'Volunteer'} <span className="text-red-500">*</span></Label>
              <Select value={dispForm.volunteer_id || '_n'} onValueChange={v => setDispForm(p => ({ ...p, volunteer_id: v === '_n' ? '' : v }))}>
                <SelectTrigger data-testid="probe-dispense-volunteer-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {volunteers.map(v => (
                    <SelectItem key={v.volunteer_id} value={v.volunteer_id}>{v.study_volunteer_code} — {v.volunteer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{lang === 'es' ? 'Visita' : 'Visit'}</Label>
              <Select value={dispForm.visit_code || '_n'} onValueChange={v => setDispForm(p => ({ ...p, visit_code: v === '_n' ? '' : v }))}>
                <SelectTrigger data-testid="probe-dispense-visit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_n">-</SelectItem>
                  {allVisits.map(v => (
                    <SelectItem key={v.visit_id} value={v.code}>{v.code} — {v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{lang === 'es' ? 'Cantidad' : 'Quantity'}</Label>
                <Input data-testid="probe-dispense-qty" type="number" min="1" max={openDispense?.stock_remaining || 1}
                  value={dispForm.quantity} onChange={e => setDispForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} />
              </div>
              <div className="space-y-1.5">
                <Label>{lang === 'es' ? 'Fecha' : 'Date'}</Label>
                <Input data-testid="probe-dispense-date" type="date"
                  value={dispForm.date} onChange={e => setDispForm(p => ({ ...p, date: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{lang === 'es' ? 'Notas' : 'Notes'}</Label>
              <Textarea data-testid="probe-dispense-notes" value={dispForm.notes}
                onChange={e => setDispForm(p => ({ ...p, notes: e.target.value }))}
                placeholder={lang === 'es' ? 'Notas opcionales...' : 'Optional notes...'}
                className="min-h-[40px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenDispense(null)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button data-testid="probe-confirm-dispense-btn" onClick={handleConfirmDispense} disabled={savingDispense}
              className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1">
              <Plus size={14} />
              {savingDispense ? '...' : (lang === 'es' ? 'Dispensar' : 'Dispense')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
